/**
 * Fetch loop for the Data Explorer browse views.
 *
 * Deliberately a thin sibling of `useExceptionDataFetch`, not a reuse:
 * that hook's envelope chain has no `items` arm, it hard-requires a
 * `dataDomain` dispatcher entry, and it sends a lifecycle `status`
 * baseline that means nothing to a browse list - bending it would touch
 * every exception view for zero gain. The generation-token pattern is
 * copied verbatim so rapid param changes / manual refetches can't
 * interleave stale writes.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import type { ExceptionUrlState } from '../config/exceptions/types'
import type { DataExplorerViewConfig } from '../config/dataExplorer'
import type { DataExplorerListResponse } from '../services/inspector/api'

interface UseDataExplorerFetchOptions {
  fpCode: string | undefined
  config: DataExplorerViewConfig
  urlState: ExceptionUrlState
  filterValues?: Record<string, unknown>
  /** API-shaped sort params (already through `toApiSortKey`). */
  sortBy: string
  sortDir: 'asc' | 'desc'
}

interface UseDataExplorerFetchReturn {
  data: Record<string, any>[]
  response: DataExplorerListResponse | null
  loading: boolean
  error: string | null
  refetch: () => void
}

export function useDataExplorerFetch({
  fpCode,
  config,
  urlState,
  filterValues,
  sortBy,
  sortDir,
}: UseDataExplorerFetchOptions): UseDataExplorerFetchReturn {
  const [data, setData] = useState<Record<string, any>[]>([])
  const [response, setResponse] = useState<DataExplorerListResponse | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  // Generation token: only the latest invocation may write state (see
  // useExceptionDataFetch for the race rationale).
  const generationRef = useRef(0)

  const run = useCallback(async () => {
    if (!fpCode) return
    const myGeneration = ++generationRef.current
    setLoading(true)
    setError(null)
    try {
      const stateWithFilters: ExceptionUrlState = {
        ...urlState,
        filterValues: filterValues ?? urlState.filterValues,
      }
      const params: Record<string, any> = {
        limit: urlState.pageSize,
        offset: urlState.page * urlState.pageSize,
        sort_by: sortBy,
        sort_dir: sortDir,
        ...(urlState.search ? { search: urlState.search } : {}),
        ...(config.buildFetchParams?.(stateWithFilters) ?? {}),
      }
      const res = await config.fetchPage(fpCode, params)
      if (myGeneration !== generationRef.current) return
      if (res) {
        setResponse(res)
        setData(res.items ?? [])
      }
    } catch (err: any) {
      if (myGeneration !== generationRef.current) return
      console.error('Failed to fetch data explorer page:', err)
      setError(err?.message || 'Failed to load data')
    } finally {
      if (myGeneration === generationRef.current) setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fpCode, config, urlState, filterValues, sortBy, sortDir])

  useEffect(() => {
    run()
    return () => {
      generationRef.current++
    }
  }, [run])

  return { data, response, loading, error, refetch: run }
}
